import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/** Próxima prestação devida (não paga) — calculada no backend a partir do mapa. */
export interface LoanNextInstallment {
  number: number;
  dueDate: string;
  amount: number;
  principalPortion: number;
  interestPortion: number;
}

/** Linha do mapa de amortização (só no detalhe — GET /api/loans/{id}). */
export interface LoanInstallment {
  number: number;
  dueDate: string;
  amount: number;
  principalPortion: number;
  interestPortion: number;
  outstandingAfter: number;
  paid: boolean;
  /** Valor real pago (do banco, pela reconciliação, ou registado à mão). */
  paidAmount?: number | null;
  paidOn?: string | null;
}

export interface Loan {
  id: number;
  financialEntity: string;
  description?: string | null;
  periodicity?: string | null;
  status?: string | null;
  // Amortização (aditivos, opcionais — empréstimo "simples" continua a funcionar).
  principal?: number | null;
  annualInterestRatePercent?: number | null;
  numberOfInstallments?: number | null;
  firstInstallmentDate?: string | null;
  frequencyMonths?: number | null;
  paidInstallmentsCount: number;
  /** Como o débito aparece no extrato — liga sozinho a prestação na reconciliação bancária. */
  bankReference?: string | null;
  // Legacy (override manual da prestação/vencimento).
  installmentValue?: number | null;
  dueDate?: string | null;
  // Computados pelo backend.
  installmentAmount?: number | null;
  outstandingBalance?: number | null;
  nextInstallment?: LoanNextInstallment | null;
  // Só preenchido no GET por id (detalhe).
  schedule?: LoanInstallment[];
  created: string;
  lastModified?: string | null;
}

/** Débitos até ao fim do horizonte (acumulado desde hoje, inclui o vencido por pagar). */
export interface DebtHorizon {
  days: number;
  until: string;
  principal: number;
  interest: number;
  total: number;
  loansTotal: number;
  confirmingTotal: number;
  /** Quanto fica em dívida na data `until`, depois de pagos os débitos até lá (legacy fora). */
  outstandingAfter: number;
  loansOutstandingAfter: number;
  confirmingOutstandingAfter: number;
}

/** Linha da posição da dívida — empréstimo em aberto ou AP de Confirming ativo. */
export interface DebtOverviewLine {
  kind: "loan" | "confirming";
  id: number;
  entity: string;
  description?: string | null;
  // null em empréstimos legacy (sem plano de amortização).
  outstandingBalance?: number | null;
  nextDueDate?: string | null;
  nextAmount?: number | null;
  // null quando o desdobramento capital/juro é desconhecido (legacy).
  nextPrincipal?: number | null;
  nextInterest?: number | null;
  isOverdue: boolean;
  remainingInstallments: number;
}

/** Posição da dívida (empréstimos + Confirming) — GET /api/loans/overview. */
export interface DebtOverview {
  today: string;
  /** Falso quando o utilizador não tem Confirming: os APs ficam de fora e o ecrã esconde-os. */
  includesConfirming: boolean;
  totalOutstanding: number;
  loansOutstanding: number;
  confirmingOutstanding: number;
  overdueTotal: number;
  /** Plafond do Confirming (só com includesConfirming). */
  confirmingPlafond?: number | null;
  confirmingPlafondUsed?: number | null;
  confirmingPlafondAvailable?: number | null;
  horizons: DebtHorizon[];
  lines: DebtOverviewLine[];
}

export interface LoanPayload {
  financialEntity: string;
  description?: string | null;
  periodicity?: string | null;
  status?: string | null;
  principal?: number | null;
  annualInterestRatePercent?: number | null;
  numberOfInstallments?: number | null;
  firstInstallmentDate?: string | null;
  frequencyMonths?: number | null;
  paidInstallmentsCount?: number;
  bankReference?: string | null;
  installmentValue?: number | null;
  dueDate?: string | null;
}

async function request<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(
      response.status,
      text || response.statusText,
    );
  }

  return (await response.json()) as T;
}

export function getLoans(): Promise<Loan[]> {
  return request<Loan[]>("/api/loans");
}

export function getDebtOverview(): Promise<DebtOverview> {
  return request<DebtOverview>("/api/loans/overview");
}

export function getLoanById(loanId: number): Promise<Loan> {
  return request<Loan>(`/api/loans/${loanId}`);
}

export function createLoan(payload: LoanPayload): Promise<Loan> {
  return request<Loan>("/api/loans", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateLoan(
  loanId: number,
  payload: LoanPayload,
): Promise<Loan> {
  return request<Loan>(`/api/loans/${loanId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

/**
 * Regista mais uma prestação paga (incrementa paidInstallmentsCount no backend).
 * Devolve o detalhe atualizado (com schedule) para refrescar saldo/próxima prestação.
 */
export function registerLoanPayment(loanId: number, amount?: number | null): Promise<Loan> {
  return request<Loan>(`/api/loans/${loanId}/register-payment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(amount != null ? { amount } : {}),
  });
}
